import { TableActionIconButton } from "_shared/_views/tables/table-toolbar";
import { TableActionButton } from "_shared/_views/tables/table-toolbar";
import AddIcon from "@mui/icons-material/Add";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { styled, Theme } from "@mui/material";


import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  type MRT_TableInstance,
  type MRT_SortingState as SortingState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Updater,
} from "material-react-table";
import { applyUpdater } from "_shared/_views/tables/table-state-updater";
import {
  MobileToolbarButtonsContainer,
  TableTitleRow,
  ScrollableTabs,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLoaderData, useNavigate } from "react-router";
import UserInformation from "users/information/user-information";
import { PageTab } from "users/networking/_views/network-page-tabs";
import NetworkInvitationInfo from "users/networking/connections/invitations/network-invitation-info";
import CreateNetworkedGroupApiRequest from "users/networking/groups/_api/commands/create-networked-group-api-command";
import UpdateNetworkedGroupApiRequest from "users/networking/groups/_api/commands/update-networked-group-api-command";
import { executeNetworkedGroupUpdate } from "users/networking/groups/_api/execute-networked-group-update";
import { networkedGroupApi, useCreateNetworkedGroupMutation, useQueryNetworkedGroupsQuery, useUpdateNetworkedGroupMutation } from "users/networking/groups/_api/networked-group-api";
import NetworkedGroupQuery, {
  NetworkedGroupFilterParameters,
  NetworkedGroupOrderField,
  NetworkedGroupOrderParameters,
} from "users/networking/groups/_api/responses/networked-groups-api-query";
import CreateNetworkedGroupComponent from "users/networking/groups/_views/create-networked-group";
import EditNetworkedGroupComponent from "users/networking/groups/_views/edit-networked-group";
import NetworkedGroupAvatarGroup from "users/networking/groups/_views/networked-group-avatar-group";
import NetworkedGroup from "users/networking/groups/networked-group";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  minWidth: 0,
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));


const columnHelper = createMRTColumnHelper<NetworkedGroup>();

/** The Name column's id: the server sorts and filters it by the group's name. */
export const NAME_COLUMN = "name";

/** The Description column's id: the server sorts and filters it by the group's description. */
export const DESCRIPTION_COLUMN = "description";

/** The Members column's id. The server neither sorts nor filters by members, so the column offers neither. */
export const MEMBERS_COLUMN = "members";

/**
 * Where a group opens: its editor, where it is renamed and re-membered. The row, the name and the ⋯'s
 * Open all go here. Undefined for a row with no group, which MRT draws while a page loads.
 */
export function groupDestination(group: NetworkedGroup): string | undefined {
  return group.id ? `/network/groups/edit/${group.id.value}` : undefined;
}

/** By name, A to Z: the order the server is asked for until the reader picks another. */
export const DEFAULT_SORTING: SortingState = [{ id: NAME_COLUMN, desc: false }];

/**
 * The server's order, from the table's sort state (#5453). Each sortable column is mapped to a field
 * of the enum, so a column id is never sent as an order field. Always one column and a direction,
 * because the server orders every page.
 */
export function networkedGroupOrderFrom(sorting: SortingState): NetworkedGroupOrderParameters {
  const first = sorting.length > 0 ? sorting[0] : DEFAULT_SORTING[0];
  return new NetworkedGroupOrderParameters(
    first.id === DESCRIPTION_COLUMN ? NetworkedGroupOrderField.Description : NetworkedGroupOrderField.Name,
    first.desc ? OrderDirection.Descending : OrderDirection.Ascending,
  );
}

/** The server's filters: the search box as `query`, and the Name and Description column filters. */
export function networkedGroupFiltersFrom(
  globalFilter: string,
  columnFilters: ColumnFiltersState,
): NetworkedGroupFilterParameters {
  const textOf = (id: string) => {
    const value = columnFilters.find((filter) => filter.id === id)?.value;
    return typeof value === "string" && value.trim() ? value.trim() : undefined;
  };
  const filterParameters = new NetworkedGroupFilterParameters(textOf(NAME_COLUMN), textOf(DESCRIPTION_COLUMN));
  filterParameters.query = globalFilter.trim() || undefined;
  return filterParameters;
}

export default function GroupsTab() {
  const handledRef = React.useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // Route
  const routeParams = useLoaderData();
  const routeAction = routeParams?.action as string | undefined;
  const routeId = routeParams?.id as string | undefined;

  // #region Queries
  const query = useMemo(() => new NetworkedGroupQuery(
    networkedGroupFiltersFrom(globalFilter, columnFilters),
    networkedGroupOrderFrom(sorting),
    PaginationParameters.create(pagination),
  ), [pagination, globalFilter, columnFilters, sorting]);

  const networkedGroupQuery = useQueryNetworkedGroupsQuery(query);
  const [createNetworkedGroupQuery] = useCreateNetworkedGroupMutation();
  const [updateNetworkedGroupQuery] = useUpdateNetworkedGroupMutation();

  const networkedGroups = useMemo(() => {
    if (!networkedGroupQuery.data) {
      return PaginatedResponse.empty<NetworkedGroup>();
    }
    return PaginatedResponse.from<NetworkedGroup>(
      NetworkedGroup.fromJSONArray(networkedGroupQuery.data?.data ?? []),
      networkedGroupQuery.data?.headers
    );
  }, [networkedGroupQuery.data]);

  const createNetworkedGroup = useCallback(async (payload: CreateNetworkedGroupApiRequest | null) => {
    if (!payload) {
      navigate(`/network/${PageTab.Groups}`);
      return;
    }

    try {
      const createdGroup = await createNetworkedGroupQuery(payload).unwrap();
      navigate(`/network/${PageTab.Groups}/edit/${createdGroup.id}`);
      enqueueSnackbar("Networked group created", {
        variant: "success"
      });
    } catch {
      enqueueSnackbar("Failed to create networked group", {
        variant: "error"
      });
    }
  }, [createNetworkedGroupQuery, navigate]);

  const openCreateGroupDialog = useCallback(() => {
    openDialog(
      {
        title: "Create Networked Group",
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "md",
          fullWidth: true,
        },
        onClose: () => {
          navigate(`/network/${PageTab.Groups}`);
        },
        component: (
          <CreateNetworkedGroupComponent
            onCreate={createNetworkedGroup}
          />
        ),
      },
    );
  }, [openDialog, createNetworkedGroup, navigate]);


  const updateNetworkedGroup = useCallback(async (
    groupId: Guid,
    payload: UpdateNetworkedGroupApiRequest | undefined
  ) => {
    if (!payload) {
      navigate(`/network/${PageTab.Groups}`);
      return;
    }

    await executeNetworkedGroupUpdate({
      update: () => updateNetworkedGroupQuery({ id: groupId, data: payload }).unwrap(),
      refresh: () => dispatch(networkedGroupApi.util.invalidateTags([
        { type: "NetworkedGroup", id: groupId.value }
      ])),
      onSuccess: () => {
        navigate(`/network/${PageTab.Groups}`);
        enqueueSnackbar("Networked group updated", { variant: "success" });
      },
      onConflict: () => {
        enqueueSnackbar("This group changed. Review the refreshed group before saving again.", {
          variant: "warning",
        });
      },
      onError: () => enqueueSnackbar("Failed to update networked group", { variant: "error" }),
    });
  }, [dispatch, updateNetworkedGroupQuery, navigate]);

  const openEditGroupDialog = useCallback((groupId: Guid) => {
    openDialog(
      {
        title: "Update Networked Group",
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "md",
          fullWidth: true,
        },
        onClose: () => {
          navigate(`/network/${PageTab.Groups}`)
        },
        component: (
          <EditNetworkedGroupComponent
            groupId={groupId}
            onSave={(payload) => updateNetworkedGroup(groupId, payload)}
          />
        ),
      },
    );
  }, [openDialog, updateNetworkedGroup, navigate]);

  const handleViewProfile = useCallback((individualId?: Guid, data?: NetworkedGroup): void => {
    if (!individualId) return;

    let individualProfile: IndividualProfile | undefined;
    if (data instanceof UserInformation) {
      individualProfile = new IndividualProfile({
        userId: data.id,
        firstName: data.name.firstName ?? "",
        lastName: data.name.lastName ?? "",
        avatarId: data.avatarId,
        legalEntityId: data.legalEntityId,
        email: data.email,
      });
    } else if (data instanceof NetworkInvitationInfo) {
      individualProfile = new IndividualProfile({
        userId: data.invitee.id,
        firstName: data.invitee.name.firstName ?? "",
        lastName: data.invitee.name.lastName ?? "",
        avatarId: data.avatarId,
        email: data.invitee.email ?? undefined,
      });
    }

    openDialog({
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: false,
      },
      component: (
        <ViewIndividualProfile
          individualId={individualId}
          profile={individualProfile}
        />
      ),
    });
  }, [openDialog]);

  // Handle URL changes
  useEffect(() => {
    // A route with no action is the dialog having closed, so forget what it opened: otherwise
    // clicking the same group again finds its key handled and opens nothing.
    if (!routeAction) {
      handledRef.current = null;
      return;
    }

    const key = `${routeAction}:${routeId}`;
    if (handledRef.current === key) return;

    if (routeAction === "new") {
      handledRef.current = key;
      openCreateGroupDialog();
      return;
    }

    if (!routeId) return;

    if (routeAction === "edit") {
      handledRef.current = key;
      openEditGroupDialog(new Guid(routeId));
      return;
    }

    if (routeAction === "view") {
      const match = networkedGroups.data.find(group => group.id?.value === routeId);
      if (!match) return;

      handledRef.current = key;
      handleViewProfile(new Guid(routeId), match);
    }
  }, [handleViewProfile, networkedGroups.data, openCreateGroupDialog, openEditGroupDialog, routeAction, routeId]);

  // The search matches a group's name and description (#6590). MRT marks the description itself.
  const renderNameCell = useCallback((group: NetworkedGroup, table: MRT_TableInstance<NetworkedGroup>) => {
    return (
      <CondensedCell
        title={
          <RowLink to={groupDestination(group)}>
            <SearchMatch text={group.name} table={table} />
          </RowLink>
        }
      />
    );
  }, []);

  const rowActions = useCallback((row: Row<NetworkedGroup>) => {
    const destination = groupDestination(row.original);
    if (!destination) return null;
    return (
      <RowActions
        ariaLabel={`Actions for ${row.original.name}`}
        open={{ to: destination }}
        actions={[]}
      />
    );
  }, []);

  const renderMembersCell = useCallback((group: NetworkedGroup) => {
    return <NetworkedGroupAvatarGroup
      group={group}
      avatarGroupMax={3}
    />
  }, []);

  // Accessor columns, because TanStack never sorts or filters a display column (#5433). The server
  // sorts and filters; the accessors only let the table offer the controls.
  const columns = useMemo(() => [
    columnHelper.accessor((group) => group.name ?? "", {
      header: "Name",
      id: NAME_COLUMN,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { kind: "name" },
      Cell: ({ row, table }) => renderNameCell(row.original, table),
    }),
    columnHelper.accessor((group) => group.description ?? "", {
      header: "Description",
      id: DESCRIPTION_COLUMN,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { kind: "text" },
      // One line, ending in an ellipsis whose hover shows the rest (#6722). The rendered value is the
      // description with the search term marked (#6590).
      Cell: ({ renderedCellValue, row }) =>
        row.original.description ? <TruncatedCell>{renderedCellValue}</TruncatedCell> : <NoValueDash />,
    }),
    columnHelper.display({
      header: "Members",
      id: MEMBERS_COLUMN,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { kind: "people" },
      Cell: ({ row }) => renderMembersCell(row.original)
    }),
  ], [renderMembersCell, renderNameCell]);

  const tableOptions: MRT_TableOptions<NetworkedGroup> = {
    columns: columns,
    data: networkedGroups.data,
    getRowId: (row) => row.id?.value ?? "",
    // A group is renamed and re-membered on its own edit page, which the row and the ⋯'s Open open.
    enableRowActions: true,
    renderRowActions: ({ row }) => rowActions(row),
    initialState: { showGlobalFilter: true, columnPinning: { right: ["mrt-row-actions"] } },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "groups", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: networkedGroupQuery.isError,
      isLoading: networkedGroupQuery.isFetching,
      onRetry: () => void networkedGroupQuery.refetch(),
      subject: "groups",
    }),
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <TableToolsMenu table={table} />
            </ToolbarButtons>
            <TableActionButton startIcon={<AddIcon />} onClick={() => navigate(`/network/${PageTab.Groups}/new`)}>
              New Group
            </TableActionButton>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitleRow>
            <TableTitle variant="h4">Network</TableTitle>
            {isMediumDisplaySize && (
              <TableActionIconButton color="primary" onClick={() => navigate("/marketplace")}>
                <AddBoxIcon fontSize="large" />
              </TableActionIconButton>
            )}
          </TableTitleRow>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={PageTab.Groups}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <TableToolsMenu table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    // The server orders by one column, and always by one, so there is neither a second sort nor none.
    enableMultiSort: false,
    enableSortingRemoval: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const destination = groupDestination(row.original);
        if (!destination) return;
        navigate(destination);
      },
      sx: { cursor: "pointer" },
    }),
    muiTableContainerProps: () => ({
      sx: {
        flex: 1
      },
    }),
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: networkedGroupQuery.isError
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount: networkedGroups.totalElements ?? 0,
    // A new search, filter or order is a new list, and a new list starts on its first page.
    onColumnFiltersChange: (updater: MRT_Updater<ColumnFiltersState>) => {
      setColumnFilters(applyUpdater(updater, columnFilters));
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setGlobalFilter(applyUpdater(updater, globalFilter) ?? "");
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    },
    state: {
      globalFilter,
      columnFilters,
      // isLoading: networkedGroupQuery.isLoading,
      isLoading: false,
      pagination,
      showAlertBanner: networkedGroupQuery.isError,
      // showProgressBars: networkedGroupQuery.isFetching,
      showProgressBars: false,
      // showSkeletons: networkedGroupQuery.isLoading,
      showSkeletons: false,
      sorting,
    },
  }

  return <MRTNoMemo<NetworkedGroup> options={tableOptions} cards={{ defaultSorting: DEFAULT_SORTING }} />;
}
